import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Accordion,
  Dropdown,
  Image,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import dash1 from "../../assets/img/docs/dashboard-1.png";
import dash2 from "../../assets/img/docs/map.png";
import dash3 from "../../assets/img/docs/dashboard-2.png";
import dash4 from "../../assets/img/docs/perHour.png";
import dash5 from "../../assets/img/docs/byMonth.png";
import dash6 from "../../assets/img/docs/topMetrics.png";
import dash7 from "../../assets/img/docs/dashboard-5.png";
import dash8 from "../../assets/img/docs/dashboard-4.png";
import dash9 from "../../assets/img/docs/dashboard-6.png";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const DashboardDocs = () => {
  const { t, i18n } = useTranslation();
  const { backgroundColor, fontColor, whitelabelUrl } = useWhitelabel();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  // Fallback to "ClickSims" if domainCompany is missing
  const brand = useMemo(
    () => activeUser?.domainCompany?.trim() || "ClickSims",
    [activeUser?.domainCompany]
  );

  return (
    <Container fluid className="dashboard-docs py-5">
      {/* Header */}
      <Row className="justify-content-between align-items-center mb-4">
        <Col>
          <h2 className="docs-title">{t("documentation.dashboard.title")}</h2>
        </Col>
      </Row>

      {/* Intro */}
      <Card className="mb-4 shadow-sm border-0 rounded-lg">
        <Card.Body>
          <Card.Title className="section-title">
            📖 {t("documentation.dashboard.title")}
          </Card.Title>
          <Card.Text>
            {t("documentation.dashboard.description", { brand })}
          </Card.Text>
          <Card.Text className="goal">
            <strong>{t("documentation.dashboard.goal")}</strong>
          </Card.Text>
        </Card.Body>
      </Card>

      {/* Statistic Cards */}
      <Card className="mb-4 shadow-sm border-0 rounded-lg">
        <Card.Body>
          <Card.Title className="section-title mb-4">
            📊 {t("documentation.dashboard.cards.title")}
          </Card.Title>
          <Row className="align-items-center">
            {/* Image */}
            <Col md={6} className="mb-3 mb-md-0">
              <Card.Img
                src={dash1}
                alt="Statistics Cards Preview"
                className="doc-image"
              />
            </Col>

            {/* Text */}
            <Col md={6}>
              <ul className="list-unstyled doc-list">
                <li>
                  <strong>
                    {t("documentation.dashboard.cards.dataVolume.title")}
                  </strong>{" "}
                  {t("documentation.dashboard.cards.dataVolume.description")}
                </li>
                <li>
                  <strong>
                    {t("documentation.dashboard.cards.dataSessions.title")}:
                  </strong>{" "}
                  {t("documentation.dashboard.cards.dataSessions.description")}
                </li>
                <li>
                  <strong>
                    {t("documentation.dashboard.cards.alerts.title")}:
                  </strong>{" "}
                  {t("documentation.dashboard.cards.alerts.description")}
                </li>
                <li>
                  <strong>
                    {t("documentation.dashboard.cards.zeroSessions.title")}:
                  </strong>{" "}
                  {t("documentation.dashboard.cards.zeroSessions.description")}
                </li>
              </ul>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Charts & Maps */}
      <Card className="mb-4 shadow-sm border-0 rounded-lg">
        <Card.Body>
          <Card.Title className="section-title">
            📈 {t("documentation.dashboard.charts.title")}
          </Card.Title>
          <Accordion flush>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                🌍 {t("documentation.dashboard.charts.dataSessionsMap.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash2}
                      alt="Data Sessions Map Preview"
                      className="doc-image"
                      fluid
                    />
                  </Col>

                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.dataSessionsMap.description"
                      )}
                    </p>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                📶{" "}
                {t("documentation.dashboard.charts.avgDataAndActiveSims.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.avgDataAndActiveSims.description"
                      )}
                    </p>
                  </Col>
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash3}
                      alt="Avg Data & Active SIMs"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>
                ⏰ {t("documentation.dashboard.charts.dataVolumePerHour.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash4}
                      alt="Data Volume per Hour"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.dataVolumePerHour.description"
                      )}
                    </p>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>
                📅 {t("documentation.dashboard.charts.monthlyDataVolume.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.monthlyDataVolume.description"
                      )}
                    </p>
                  </Col>
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash5}
                      alt="Data Volume per Hour"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                🏆 {t("documentation.dashboard.charts.topMetrics.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash6}
                      alt="Top Metrics – Data Sessions"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.topMetrics.description"
                      )}
                    </p>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5">
              <Accordion.Header>
                📊 {t("documentation.dashboard.charts.simCardsPerOffer.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.simCardsPerOffer.description"
                      )}
                    </p>
                  </Col>
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash8}
                      alt="SIM Cards per Offer - SIM Cards per Status
                Distribution"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>
                📡{" "}
                {t("documentation.dashboard.charts.dataVolumePerNetwork.title")}
                Distribution
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash7}
                      alt="📡 Data Volume per Network - Active SIM Cards per Network Distribution"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t(
                        "documentation.dashboard.charts.dataVolumePerNetwork.description"
                      )}
                    </p>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="7">
              <Accordion.Header>
                ⚠️ {t("documentation.dashboard.charts.alerts.title")}
              </Accordion.Header>
              <Accordion.Body>
                <Row className="align-items-center">
                  {/* Text */}
                  <Col md={6}>
                    <p className="doc-text">
                      {t("documentation.dashboard.charts.alerts.description")}
                    </p>
                    <ul className="doc-list">
                      <li>
                        {t(
                          "documentation.dashboard.charts.alerts.types.warning"
                        )}
                      </li>
                      <li>
                        {t(
                          "documentation.dashboard.charts.alerts.types.depleted"
                        )}
                      </li>
                    </ul>
                    <p className="doc-text">
                      {t("documentation.dashboard.charts.alerts.note")}
                    </p>
                  </Col>
                  {/* Image */}
                  <Col md={6} className="mb-3 mb-md-0">
                    <Image
                      src={dash9}
                      alt="⚠️ Consumption Alerts - WatchDog monitoring"
                      className="doc-image"
                      fluid
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Card.Body>
      </Card>

      {/* Utility */}
      <Card className="mb-4 shadow-sm border-0 rounded-lg">
        <Card.Body>
          <Card.Title className="section-title">
            ⚙️ {t("documentation.dashboard.utility.title")}
          </Card.Title>
          <Card.Text>
            {t("documentation.dashboard.utility.description")}
          </Card.Text>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DashboardDocs;
